import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { Search, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from './contexts/CartContext';

interface SpaceImage {
  id: number;
  url: string;
  title: string;
  items: ProductItem[];
}

interface ProductItem {
  id: number;
  name: string;
  price: string;
  image: string;
  category: string;
}

const AISpaceCreator: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();
  const cartCount = getCartCount();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSpaces, setGeneratedSpaces] = useState<SpaceImage[]>([]);
  const [selectedSpace, setSelectedSpace] = useState<SpaceImage | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Placeholder suggestions that rotate
  const placeholders = [
    "A minimalist living room with modern furniture and warm lighting...",
    "A cozy bedroom with Scandinavian design and natural materials...",
    "An industrial loft space with exposed brick and vintage decor...",
    "A bohemian reading nook with plants and earthy tones...",
    "A contemporary office with sleek desk and ergonomic chair...",
    "A serene meditation space with neutral colors and soft textures..."
  ];

  // Rotate placeholders every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [placeholders.length]);

  // Mock data for inspiration
  const inspirationSpaces: SpaceImage[] = [
    {
      id: 1,
      url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=800&fit=crop&auto=format',
      title: 'Minimalist Living Room',
      items: []
    },
    {
      id: 2,
      url: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=600&h=400&fit=crop&auto=format',
      title: 'Modern Bedroom',
      items: []
    },
    {
      id: 3,
      url: 'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=600&h=700&fit=crop&auto=format',
      title: 'Cozy Reading Nook',
      items: []
    },
    {
      id: 4,
      url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=500&fit=crop&auto=format',
      title: 'Scandinavian Office',
      items: []
    },
    {
      id: 5,
      url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=600&fit=crop&auto=format',
      title: 'Bohemian Space',
      items: []
    },
    {
      id: 6,
      url: 'https://images.unsplash.com/photo-1538688423619-a81d3f23454b?w=600&h=750&fit=crop&auto=format',
      title: 'Industrial Loft',
      items: []
    }
  ];

  // Mock generated results
  const mockGeneratedSpaces: SpaceImage[] = [
    {
      id: 101,
      url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop&auto=format',
      title: 'Curated Living Space',
      items: [
        { id: 1001, name: 'Modern Velvet Sofa', price: '$1,299', image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=400&fit=crop&auto=format', category: 'Furniture' },
        { id: 1002, name: 'Ceramic Table Lamp', price: '$189', image: 'https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=400&h=400&fit=crop&auto=format', category: 'Lighting' },
        { id: 1003, name: 'Geometric Rug', price: '$449', image: 'https://images.unsplash.com/photo-1600166898405-da9535204843?w=400&h=400&fit=crop&auto=format', category: 'Decor' }
      ]
    },
    {
      id: 102,
      url: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=800&h=600&fit=crop&auto=format',
      title: 'Contemporary Setup',
      items: [
        { id: 1004, name: 'Designer Armchair', price: '$899', image: 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=400&h=400&fit=crop&auto=format', category: 'Furniture' },
        { id: 1005, name: 'Abstract Wall Art', price: '$329', image: 'https://images.unsplash.com/photo-1561214115-f2f134cc4912?w=400&h=400&fit=crop&auto=format', category: 'Art' },
        { id: 1006, name: 'Floor Lamp', price: '$279', image: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?w=400&h=400&fit=crop&auto=format', category: 'Lighting' }
      ]
    },
    {
      id: 103,
      url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop&auto=format',
      title: 'Elegant Interior',
      items: [
        { id: 1007, name: 'Leather Sofa', price: '$2,199', image: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=400&h=400&fit=crop&auto=format', category: 'Furniture' },
        { id: 1008, name: 'Pendant Light', price: '$399', image: 'https://images.unsplash.com/photo-1524484485831-a92ffc0de03f?w=400&h=400&fit=crop&auto=format', category: 'Lighting' },
        { id: 1009, name: 'Coffee Table', price: '$649', image: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?w=400&h=400&fit=crop&auto=format', category: 'Furniture' }
      ]
    },
    {
      id: 104,
      url: 'https://images.unsplash.com/photo-1538688423619-a81d3f23454b?w=800&h=600&fit=crop&auto=format',
      title: 'Modern Aesthetic',
      items: [
        { id: 1010, name: 'Accent Chair', price: '$729', image: 'https://images.unsplash.com/photo-1519961655-69e19787eeef?w=400&h=400&fit=crop&auto=format', category: 'Furniture' },
        { id: 1011, name: 'Decorative Vase', price: '$129', image: 'https://images.unsplash.com/photo-1578500494198-246f612d3b3d?w=400&h=400&fit=crop&auto=format', category: 'Decor' }
      ]
    }
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setShowResults(false);
    setSelectedSpace(null);

    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));

    setGeneratedSpaces(mockGeneratedSpaces);
    setShowResults(true);
    setIsGenerating(false);

    // Animate cards in
    setTimeout(() => {
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.fromTo(card,
            { opacity: 0, y: 50, scale: 0.9 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.6,
              delay: index * 0.1,
              ease: 'power3.out'
            }
          );
        }
      });
    }, 100);
  };

  const handleSpaceClick = (space: SpaceImage) => {
    setSelectedSpace(space);

    // Scroll to results section smoothly
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleAddToCart = (item: ProductItem) => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/10">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 md:px-8 py-3 sm:py-5 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 sm:gap-2 text-black hover:text-black/60 transition-colors"
          >
            <ArrowLeft className="w-4 sm:w-5 h-4 sm:h-5 stroke-[1.5]" />
            <span className="text-xs sm:text-sm font-light tracking-wide">BACK</span>
          </button>
          <h1 className="text-sm sm:text-base md:text-lg font-light tracking-widest text-black uppercase">Create Space</h1>
          <button
            onClick={() => navigate('/cart')}
            className="relative flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2 border border-black hover:bg-black hover:text-white transition-all"
          >
            <ShoppingBag className="w-3.5 sm:w-4 h-3.5 sm:h-4 stroke-[1.5]" />
            <span className="hidden sm:inline text-sm font-light tracking-wide">BAG</span>
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-20 sm:pt-28 pb-12 sm:pb-20 px-3 sm:px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Prompt Input Area - Google Style */}
          <div className="mb-16">
            {/* Suggestions above search bar */}
            <div className="text-center mb-8">
              <p className="text-xs text-black/40 tracking-wide mb-2 uppercase font-light">
                Try searching for
              </p>
              <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white border border-black/10 rounded-full text-[10px] sm:text-xs text-black/60 hover:border-black/30 transition-all cursor-pointer">
                  Modern living room
                </span>
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white border border-black/10 rounded-full text-[10px] sm:text-xs text-black/60 hover:border-black/30 transition-all cursor-pointer">
                  Cozy bedroom
                </span>
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white border border-black/10 rounded-full text-[10px] sm:text-xs text-black/60 hover:border-black/30 transition-all cursor-pointer">
                  Minimalist office
                </span>
                <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white border border-black/10 rounded-full text-[10px] sm:text-xs text-black/60 hover:border-black/30 transition-all cursor-pointer">
                  Reading nook
                </span>
              </div>
            </div>

            {/* Google-style search bar */}
            <div className="max-w-3xl mx-auto">
              <div className="relative bg-white rounded-full border border-black/20 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-5">
                  {/* Search Icon */}
                  <Search className="w-5 h-5 text-black/60 flex-shrink-0 stroke-[2]" />

                  {/* Input */}
                  <input
                    type="text"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    placeholder={placeholders[placeholderIndex]}
                    className="flex-1 bg-transparent focus:outline-none text-black placeholder-black/40 text-base font-light"
                  />

                  {/* Generate Button */}
                  {prompt.trim() && (
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="px-6 py-2 bg-black text-white text-sm font-light tracking-wide hover:bg-black/80 disabled:bg-black/40 disabled:cursor-not-allowed transition-all duration-200 rounded-full flex-shrink-0"
                    >
                      {isGenerating ? 'Generating...' : 'Generate'}
                    </button>
                  )}
                </div>
              </div>

              {/* Helper text */}
              <div className="text-center mt-4">
                <p className="text-xs text-black/40 font-light">
                  Describe your dream space and we'll curate the perfect items for you
                </p>
              </div>
            </div>

            {/* Loading State */}
            {isGenerating && (
              <div className="mt-8 max-w-2xl mx-auto bg-white border border-black/10 rounded-2xl p-6 shadow-md">
                <div className="flex items-center gap-4">
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <div className="absolute inset-0 border-2 border-black/10 rounded-full"></div>
                    <div className="absolute inset-0 border-t-2 border-t-black rounded-full animate-spin"></div>
                  </div>
                  <div>
                    <p className="text-black font-light text-sm">Generating curated spaces...</p>
                    <p className="text-xs text-black/50 mt-1 font-light">Finding perfect items for you</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Generated Results */}
          {showResults && (
            <div ref={resultsRef} className="mb-10">
              <h3 className="text-sm sm:text-base font-light tracking-widest text-black mb-3 sm:mb-4 uppercase">Your Spaces</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                {generatedSpaces.map((space, index) => (
                  <div
                    key={space.id}
                    ref={el => { cardsRef.current[index] = el; }}
                    onClick={() => handleSpaceClick(space)}
                    className="group cursor-pointer bg-white border border-black/10 hover:border-black transition-all duration-300 rounded-lg overflow-hidden"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={space.url}
                        alt={space.title}
                        className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                    <div className="p-2.5 border-t border-black/10">
                      <h4 className="text-xs font-light tracking-widest text-black uppercase">
                        {space.title}
                      </h4>
                      <p className="text-[10px] text-black/50 mt-0.5 tracking-wide">
                        {space.items.length} items
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected Space Items */}
              {selectedSpace && selectedSpace.items.length > 0 && (
                <div className="mt-4 sm:mt-6 border border-black/20 p-3 sm:p-5 rounded-lg">
                  <h4 className="text-xs sm:text-sm font-light tracking-widest text-black mb-3 sm:mb-4 uppercase">Curated Items</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                    {selectedSpace.items.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white border border-black/10 hover:border-black transition-all duration-300 rounded-lg overflow-hidden"
                      >
                        <div className="aspect-square overflow-hidden border-b border-black/10">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover grayscale-[30%] hover:grayscale-0 transition-all duration-500"
                          />
                        </div>
                        <div className="p-3">
                          <span className="text-[9px] font-light tracking-widest text-black/60 uppercase">
                            {item.category}
                          </span>
                          <h5 className="text-xs font-light text-black mt-1.5 tracking-wide line-clamp-1">{item.name}</h5>
                          <p className="text-sm font-light text-black mt-1 tracking-wide">{item.price}</p>
                          <button
                            onClick={() => handleAddToCart(item)}
                            className="w-full mt-3 px-3 py-2 border border-black text-black hover:bg-black hover:text-white transition-all flex items-center justify-center gap-1.5 rounded-md"
                          >
                            <ShoppingBag className="w-3.5 h-3.5 stroke-[1.5]" />
                            <span className="text-[10px] font-light tracking-widest uppercase">Add to Bag</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inspiration Section */}
          <div>
            <h3 className="text-xs sm:text-sm font-light tracking-widest text-black mb-1 sm:mb-1.5 uppercase">
              {showResults ? 'More Inspiration' : 'Get Inspired'}
            </h3>
            <p className="text-black/50 text-[9px] sm:text-[10px] tracking-wide mb-3 sm:mb-4 uppercase">Curated community spaces</p>

            {/* Compact Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-2.5">
              {inspirationSpaces.slice(0, 6).map((space) => (
                <div
                  key={space.id}
                  className="bg-white border border-black/10 hover:border-black transition-all duration-300 cursor-pointer group rounded-lg overflow-hidden"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={space.url}
                      alt={space.title}
                      className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="p-2 border-t border-black/10">
                    <span className="text-[10px] font-light tracking-widest text-black uppercase line-clamp-1">{space.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISpaceCreator;
